import React, { useCallback, useEffect, useMemo, useState } from 'react';
import {
  Box,
  Typography,
  CircularProgress,
  Table,
  TableBody,
  TableCell,
  TableContainer,
  TableHead,
  TableRow,
  Collapse,
  IconButton,
  Button,
  Dialog,
  DialogTitle,
  DialogContent,
  DialogActions,
  TextField,
  Switch,
  Tooltip,
} from '@mui/material';
import KeyboardArrowDownIcon from '@mui/icons-material/KeyboardArrowDown';
import KeyboardArrowUpIcon from '@mui/icons-material/KeyboardArrowUp';
import CloseIcon from '@mui/icons-material/Close';
import InfoOutlinedIcon from '@mui/icons-material/InfoOutlined';
import LockIcon from '@mui/icons-material/Lock';
import LockOpenIcon from '@mui/icons-material/LockOpen';
import ReactApexChart from 'react-apexcharts';
import { getProgramTransactions, getPmsProgramReportByTransaction, getExcessReasonBreakdown, updateProgramTransactionTimes, processSingleTransaction, getMachineProgramSummary, updateShiftProductionFlag, getCycleTimeCalculation, unlockCycleTime, getExtraIdleTimeCalculation } from '../../../Services/app/oeeservice';
import { timeToSeconds } from '../../../Shared/utils/holidaycalculation';

const HMS_PATTERN = /^\d{1,2}:\d{2}:\d{2}$/;

const BORDER = '#ECEFF3';
const TEXT_MUTED = '#898781';
const STATUS_CRITICAL = '#d03b3b';

// Same run%-vs-cyc_time rule as the Low/Normal/High Cycle columns
// (<60% low, 60-130% normal, >130% high; no cyc_time budget -> normal) -
// applied per part here since part_detail already carries each part's own
// cyc_time.
const BUCKET_COLOR = { low: '#eb6834', normal: '#0ca30c', high: '#d03b3b' };

// Bucketed against the TRANSACTION's own cycle_time (uniform target for
// every part), not each part's own externally-fed cyc_time - matches the
// backend's Low/Normal/High Cycle columns exactly (see
// Machine.service.ts's bucketPartsByTarget).
const cycleBucket = (part, cycleTimeTarget) => {
  if (!cycleTimeTarget || cycleTimeTarget <= 0) return 'normal';
  const pct = (Number(part?.run_time) || 0) / cycleTimeTarget * 100;
  if (pct < 60) return 'low';
  if (pct > 130) return 'high';
  return 'normal';
};

const BucketChip = ({ bucket }) => (
  <Box
    component="span"
    sx={{
      display: 'inline-block',
      px: 1,
      py: 0.25,
      borderRadius: '10px',
      fontSize: '12px',
      fontWeight: 600,
      color: '#fff',
      background: BUCKET_COLOR[bucket] || TEXT_MUTED,
      textTransform: 'capitalize',
    }}
  >
    {bucket}
  </Box>
);

const StatusBox = ({ children }) => (
  <Box
    sx={{
      p: 6,
      border: `1px solid ${BORDER}`,
      borderRadius: 2,
      background: '#ffffff',
      display: 'flex',
      flexDirection: 'column',
      alignItems: 'center',
      justifyContent: 'center',
      color: TEXT_MUTED,
      gap: 1,
    }}
  >
    {children}
  </Box>
);

// en-IN gives DD/MM/YYYY (day-month-year) instead of the browser-default
// locale's M/D/YYYY.
const INDIAN_DATE_OPTS = { day: '2-digit', month: '2-digit', year: 'numeric', hour: '2-digit', minute: '2-digit', second: '2-digit', hour12: true };

const fmtTime = (val) => {
  if (!val) return '—';
  const num = Number(val);
  if (!isNaN(num) && num > 0) return new Date(num).toLocaleString('en-IN', INDIAN_DATE_OPTS);
  const d = new Date(val);
  return isNaN(d.getTime()) ? String(val) : d.toLocaleString('en-IN', INDIAN_DATE_OPTS);
};

// Date-only version (no time) for the shift-level "Date" column, e.g.
// "2026-08-31T18:30:00.000Z" -> "31/08/2026".
const fmtDate = (val) => {
  if (!val) return '—';
  const d = new Date(val);
  return isNaN(d.getTime()) ? String(val) : d.toLocaleDateString('en-IN', { day: '2-digit', month: '2-digit', year: 'numeric' });
};

const secondsToHms = (totalSeconds) => {
  if (totalSeconds === null || totalSeconds === undefined || isNaN(totalSeconds)) return '—';
  const abs = Math.abs(Math.round(Number(totalSeconds)));
  const hours = Math.floor(abs / 3600);
  const minutes = Math.floor((abs % 3600) / 60);
  const seconds = abs % 60;
  return `${String(hours).padStart(2, '0')}:${String(minutes).padStart(2, '0')}:${String(seconds).padStart(2, '0')}`;
};

const fmtCost = (value) =>
  value === null || value === undefined || isNaN(value) ? '—' : `₹${Math.round(Number(value)).toLocaleString('en-IN')}`;

// Cumulative idle-reason time for a shift: break_time is [{name, duration}]
// per reason, already summed per-reason for that shift - this sums across
// all reasons to give one total.
const sumIdleReasonTime = (breakTime) =>
  Array.isArray(breakTime) ? breakTime.reduce((sum, r) => sum + (Number(r.duration) || 0), 0) : 0;

const secondsToShortDuration = (totalSeconds) => {
  if (totalSeconds === null || totalSeconds === undefined || isNaN(totalSeconds)) return '—';
  const abs = Math.abs(Math.round(Number(totalSeconds)));
  const hours = Math.floor(abs / 3600);
  const minutes = Math.floor((abs % 3600) / 60);
  const seconds = abs % 60;
  const parts = [];
  if (hours > 0) parts.push(`${hours}h`);
  if (minutes > 0) parts.push(`${minutes}m`);
  if (seconds > 0 || parts.length === 0) parts.push(`${seconds}s`);
  return parts.join(' ');
};

// Idle Reason Pareto chart - bars are per-reason total idle duration
// (descending), the line is cumulative % of total, sourced live from
// GET /machine/excess-reason-breakdown for the exact [start_time, end_time)
// window passed in (a program_transaction's own window, or one of its child
// shifts) - already per-reason excess (duration minus threshold, enabled
// reasons excluded), so the chart's total always matches the row's own
// Excess Reason Time column exactly.
function IdleReasonParetoModal({ open, onClose, machine, loading, error, data }) {
  const chartData = useMemo(() => {
    if (!data || !Array.isArray(data) || data.length === 0) return null;

    const sorted = [...data].sort((a, b) => (Number(b.excess) || 0) - (Number(a.excess) || 0));
    const categories = sorted.map((item) => item.name || 'Uncategorized');
    const barSeries = sorted.map((item) => Number(item.excess) || 0);
    const allowedSeries = sorted.map((item) => Number(item.threshold) || 0);
    const hasAllowedTime = allowedSeries.some((secs) => secs > 0);
    // Both time-based bars need an identical shared scale (not just two
    // independently auto-ranged axes that happen to look similar) so their
    // heights are directly, honestly comparable - explicit max with a
    // little headroom, reused on both the visible and the hidden-duplicate
    // yaxis entry below.
    const sharedMax = Math.max(...barSeries, ...allowedSeries, 1);
    const totalSecs = barSeries.reduce((acc, secs) => acc + secs, 0);

    let cum = 0;
    const lineSeries = barSeries.map((secs) => {
      cum += secs;
      return totalSecs > 0 ? Number(((cum / totalSecs) * 100).toFixed(1)) : 0;
    });

    return {
      categories,
      totalDuration: secondsToShortDuration(totalSecs),
      series: [
        { name: 'Excess Reason Time', type: 'column', data: barSeries },
        ...(hasAllowedTime ? [{ name: 'Allowed Time', type: 'column', data: allowedSeries }] : []),
        { name: 'Cumulative %', type: 'line', data: lineSeries },
      ],
      options: {
        chart: { type: 'line', toolbar: { show: true, tools: { pan: false } }, fontFamily: 'inherit' },
        colors: hasAllowedTime ? ['#2563eb', '#22c55e', '#dc2626'] : ['#2563eb', '#dc2626'],
        stroke: { width: hasAllowedTime ? [0, 0, 3] : [0, 3], curve: 'smooth' },
        plotOptions: { bar: { columnWidth: '45%', borderRadius: 4 } },
        xaxis: {
          categories,
          labels: {
            rotate: -45,
            rotateAlways: true,
            trim: false,
            hideOverlappingLabels: false,
            style: { fontSize: '11px', fontWeight: 600 },
            formatter: (value) => {
              const words = String(value).trim().split(/\s+/);
              return words.length > 1 ? `${words[0]}...` : words[0];
            },
          },
        },
        grid: { padding: { bottom: 40 } },
        yaxis: [
          {
            seriesName: 'Excess Reason Time',
            title: { text: 'Excess Reason Time', style: { color: '#2563eb', fontWeight: 600 } },
            min: 0,
            max: sharedMax,
            labels: { formatter: (val) => secondsToShortDuration(val) },
          },
          ...(hasAllowedTime ? [{ seriesName: 'Allowed Time', show: false, min: 0, max: sharedMax }] : []),
          {
            seriesName: 'Cumulative %',
            opposite: true,
            title: { text: 'Cumulative %', style: { color: '#dc2626', fontWeight: 600 } },
            min: 0,
            max: 100,
            labels: { formatter: (val) => `${val}%` },
          },
        ],
        tooltip: {
          shared: true,
          intersect: false,
          x: { formatter: (val, { dataPointIndex }) => categories[dataPointIndex] ?? val },
          y: { formatter: (val, { seriesIndex }) => (seriesIndex < (hasAllowedTime ? 2 : 1) ? secondsToShortDuration(val) : `${val}%`) },
        },
        legend: { position: 'top' },
      },
    };
  }, [data]);

  return (
    <Dialog open={open} onClose={onClose} maxWidth="md" fullWidth>
      <DialogTitle sx={{ display: 'flex', justifyContent: 'space-between', alignItems: 'center', pb: 1 }}>
        <Box>
          <Typography variant="h6" sx={{ fontWeight: 700 }}>
            Excess Reason Time — {machine}
          </Typography>
          {chartData && (
            <Typography variant="caption" sx={{ color: 'text.secondary' }}>
              Total Excess: {chartData.totalDuration}
            </Typography>
          )}
        </Box>
        <IconButton onClick={onClose} size="small" sx={{ color: (theme) => theme.palette.grey[500] }}>
          <CloseIcon />
        </IconButton>
      </DialogTitle>
      <DialogContent dividers>
        {loading ? (
          <Box sx={{ display: 'flex', justifyContent: 'center', alignItems: 'center', minHeight: 300 }}>
            <CircularProgress size={36} />
          </Box>
        ) : error ? (
          <Typography color="error" sx={{ p: 2 }}>{error}</Typography>
        ) : !chartData || chartData.categories.length === 0 ? (
          <Typography sx={{ p: 2, color: 'text.secondary', textAlign: 'center' }}>
            No excess reason time for machine {machine} in this window.
          </Typography>
        ) : (
          <Box sx={{ pt: 1 }}>
            <ReactApexChart options={chartData.options} series={chartData.series} type="line" height={500} />
          </Box>
        )}
      </DialogContent>
    </Dialog>
  );
}

// Update button on the Method 4 drilldown: cycle_time/handling_time/
// setup_time are usually 0 (nothing populates them automatically) - this
// lets the user set them directly via GET /machine/update-program-transaction-times.
function EditTimesDialog({ open, onClose, row, onSaved }) {
  const [form, setForm] = useState({ cycle_time: '00:00:00', handling_time: '00:00:00', setup_time: '00:00:00' });
  const [saving, setSaving] = useState(false);
  const [stage, setStage] = useState('Saving...');
  const [errors, setErrors] = useState({});

  useEffect(() => {
    if (open && row) {
      setForm({
        cycle_time: secondsToHms(row.cycle_time),
        handling_time: secondsToHms(row.handling_time),
        setup_time: secondsToHms(row.setup_time),
      });
      setErrors({});
    }
  }, [open, row]);

  const handleChange = (field) => (e) => {
    setForm((prev) => ({ ...prev, [field]: e.target.value }));
  };

  const handleSave = async () => {
    const nextErrors = {};
    ['cycle_time', 'handling_time', 'setup_time'].forEach((field) => {
      if (!HMS_PATTERN.test(form[field])) nextErrors[field] = 'Use HH:MM:SS format';
    });
    if (Object.keys(nextErrors).length > 0) {
      setErrors(nextErrors);
      return;
    }

    setSaving(true);
    setStage('Saving...');
    try {
      await updateProgramTransactionTimes(
        row.id,
        timeToSeconds(form.cycle_time),
        timeToSeconds(form.handling_time),
        timeToSeconds(form.setup_time),
      );
      // Reprocess this one transaction so low_cycle/normal_cycle/high_cycle
      // (gated on cycle_time) and extra_setting_time (gated on setup_time)
      // reflect the new values immediately, not after a separate manual
      // process-program-transactions run.
      setStage('Reprocessing...');
      await processSingleTransaction(row.id);
      setSaving(false);
      onSaved();
    } catch (err) {
      setSaving(false);
      setErrors({ form: 'Failed to save. Please try again.' });
    }
  };

  return (
    <Dialog open={open} onClose={onClose} maxWidth="xs" fullWidth>
      <DialogTitle sx={{ display: 'flex', justifyContent: 'space-between', alignItems: 'center', pb: 1 }}>
        <Typography variant="h6" sx={{ fontWeight: 700 }}>Update Transaction Times</Typography>
        <IconButton onClick={onClose} size="small" sx={{ color: (theme) => theme.palette.grey[500] }}>
          <CloseIcon />
        </IconButton>
      </DialogTitle>
      <DialogContent dividers sx={{ display: 'flex', flexDirection: 'column', gap: 2, pt: 2 }}>
        <TextField
          label="Cycle Time"
          placeholder="HH:MM:SS"
          value={form.cycle_time}
          onChange={handleChange('cycle_time')}
          error={!!errors.cycle_time}
          helperText={errors.cycle_time}
          fullWidth
          size="small"
        />
        <TextField
          label="Handling Time"
          placeholder="HH:MM:SS"
          value={form.handling_time}
          onChange={handleChange('handling_time')}
          error={!!errors.handling_time}
          helperText={errors.handling_time}
          fullWidth
          size="small"
        />
        <TextField
          label="Setup Time"
          placeholder="HH:MM:SS"
          value={form.setup_time}
          onChange={handleChange('setup_time')}
          error={!!errors.setup_time}
          helperText={errors.setup_time}
          fullWidth
          size="small"
        />
        {errors.form && <Typography color="error" sx={{ fontSize: '13px' }}>{errors.form}</Typography>}
      </DialogContent>
      <DialogActions>
        <Button onClick={onClose} disabled={saving}>Cancel</Button>
        <Button onClick={handleSave} disabled={saving} variant="contained" sx={{ gap: 1 }}>
          {saving && <CircularProgress size={16} sx={{ color: '#fff' }} />}
          {saving ? stage : 'Save'}
        </Button>
      </DialogActions>
    </Dialog>
  );
}

function ShiftRows({ transactionId, startTimeMs, endTimeMs, onDataChanged, cycleTime, showNonMandatoryNet }) {
  const [rows, setRows] = useState([]);
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState('');

  const loadShifts = useCallback(async () => {
    setLoading(true);
    setError('');
    try {
      const data = await getPmsProgramReportByTransaction(transactionId, startTimeMs, endTimeMs);
      setRows(Array.isArray(data) ? data : []);
    } catch (err) {
      setError('Failed to load shift report. Please try again.');
    } finally {
      setLoading(false);
    }
  }, [transactionId, startTimeMs, endTimeMs]);

  useEffect(() => {
    let cancelled = false;
    (async () => {
      if (cancelled) return;
      await loadShifts();
    })();
    return () => { cancelled = true; };
  }, [loadShifts]);

  if (loading) {
    return (
      <Box sx={{ p: 2, display: 'flex', justifyContent: 'center' }}>
        <CircularProgress size={20} />
      </Box>
    );
  }
  if (error) {
    return (
      <Box sx={{ p: 2, textAlign: 'center', color: STATUS_CRITICAL, fontSize: '13px' }}>{error}</Box>
    );
  }
  if (rows.length === 0) {
    return (
      <Box sx={{ p: 2, textAlign: 'center', color: TEXT_MUTED, fontSize: '13px' }}>
        No shift rows saved for this transaction.
      </Box>
    );
  }

  return (
    <Box sx={{ p: 2, background: '#fafafa' }}>
      <TableContainer>
        <Table size="small">
          <TableHead>
            <TableRow sx={{ background: '#f4f6f9' }}>
              <TableCell />
              <TableCell sx={{ fontWeight: 700 }}>Shift</TableCell>
              <TableCell sx={{ fontWeight: 700 }}>Date</TableCell>
              <TableCell sx={{ fontWeight: 700 }}>Start</TableCell>
              <TableCell sx={{ fontWeight: 700 }}>End</TableCell>
              <TableCell align="right" sx={{ fontWeight: 700 }}>Total Run Time</TableCell>
              <TableCell align="right" sx={{ fontWeight: 700 }}>Total Idle Time</TableCell>
              <TableCell align="right" sx={{ fontWeight: 700 }}>Total Stop Time</TableCell>
              <TableCell align="right" sx={{ fontWeight: 700 }}>Total Alarm Time</TableCell>
              <TableCell align="right" sx={{ fontWeight: 700 }}>Idle Time with Reason</TableCell>
              {showNonMandatoryNet && <TableCell align="right" sx={{ fontWeight: 700 }}>Non Mandatory Idle Time</TableCell>}
              {!showNonMandatoryNet && <TableCell align="right" sx={{ fontWeight: 700 }}>Non Mandatory Idle Time1</TableCell>}
              {!showNonMandatoryNet && <TableCell align="right" sx={{ fontWeight: 700 }}>Non Mandatory Idle Time1 (Net)</TableCell>}
              <TableCell align="right" sx={{ fontWeight: 700 }}>Lower Cycle Time</TableCell>
              <TableCell align="right" sx={{ fontWeight: 700 }}>Normal Cycle Time</TableCell>
              <TableCell align="right" sx={{ fontWeight: 700 }}>Higher Cycle Time</TableCell>
              <TableCell align="right" sx={{ fontWeight: 700 }}>Additional Parts</TableCell>
              <TableCell align="right" sx={{ fontWeight: 700 }}>Actual Production</TableCell>
              <TableCell align="center" sx={{ fontWeight: 700 }}>Production</TableCell>
            </TableRow>
          </TableHead>
          <TableBody>
            {rows.map((row) => (
              <ShiftRow
                key={row.id}
                row={row}
                cycleTime={cycleTime}
                showNonMandatoryNet={showNonMandatoryNet}
                onToggled={async () => {
                  await loadShifts();
                  if (onDataChanged) onDataChanged();
                }}
              />
            ))}
          </TableBody>
        </Table>
      </TableContainer>
    </Box>
  );
}

function ShiftRow({ row, onToggled, cycleTime, showNonMandatoryNet }) {
  const [expanded, setExpanded] = useState(false);
  const [toggling, setToggling] = useState(false);
  const [calcOpen, setCalcOpen] = useState(false);
  const parts = Array.isArray(row.part_detail) ? row.part_detail : [];
  const idleReasons = Array.isArray(row.break_time) ? row.break_time : [];
  const isProduction = row.is_production !== false;

  const handleToggle = async () => {
    setToggling(true);
    try {
      await updateShiftProductionFlag(row.id, !isProduction);
      if (onToggled) await onToggled();
    } catch (err) {
      console.error('Failed to toggle shift production flag', err);
    } finally {
      setToggling(false);
    }
  };

  return (
    <>
      <TableRow
        hover
        sx={{ cursor: 'pointer', opacity: isProduction ? 1 : 0.5, background: isProduction ? undefined : '#f4f4f4' }}
        onClick={() => setExpanded((v) => !v)}
      >
        <TableCell>
          <IconButton size="small">
            {expanded ? <KeyboardArrowUpIcon /> : <KeyboardArrowDownIcon />}
          </IconButton>
        </TableCell>
        <TableCell>{row.shift_no ?? '—'}</TableCell>
        <TableCell>{fmtDate(row.date)}</TableCell>
        <TableCell>{fmtTime(row.start_time)}</TableCell>
        <TableCell>{fmtTime(row.end_time)}</TableCell>
        <TableCell align="right">{secondsToHms(row.run_time)}</TableCell>
        <TableCell align="right">{secondsToHms(row.idle_time)}</TableCell>
        <TableCell align="right">{secondsToHms(row.stop_time)}</TableCell>
        <TableCell align="right">{secondsToHms(row.alarm_time)}</TableCell>
        <TableCell align="right">{secondsToHms(sumIdleReasonTime(row.break_time))}</TableCell>
        {showNonMandatoryNet && (
          <TableCell
            align="right"
            sx={{ color: Number(row.excess_reason_time) > 0 ? STATUS_CRITICAL : undefined, fontWeight: Number(row.excess_reason_time) > 0 ? 700 : 400 }}
          >
            {secondsToHms(row.excess_reason_time)}
          </TableCell>
        )}
        {!showNonMandatoryNet && (
          <TableCell
            align="right"
            sx={{ color: Number(row.extra_idle_time) > 0 ? STATUS_CRITICAL : undefined, fontWeight: Number(row.extra_idle_time) > 0 ? 700 : 400 }}
          >
            {secondsToHms(row.extra_idle_time)}
            <Tooltip title="How was this calculated?">
              <IconButton
                size="small"
                onClick={(e) => { e.stopPropagation(); setCalcOpen(true); }}
                sx={{ p: 0.25, ml: 0.25 }}
              >
                <InfoOutlinedIcon sx={{ fontSize: 14, color: TEXT_MUTED }} />
              </IconButton>
            </Tooltip>
          </TableCell>
        )}
        {!showNonMandatoryNet && (
          <TableCell
            align="right"
            sx={{ color: Number(row.extra_idle_time_net) > 0 ? STATUS_CRITICAL : undefined, fontWeight: Number(row.extra_idle_time_net) > 0 ? 700 : 400 }}
          >
            {secondsToHms(row.extra_idle_time_net)}
          </TableCell>
        )}
        <TableCell align="right">{row.low_cycle ?? '—'}</TableCell>
        <TableCell align="right">{row.normal_cycle ?? '—'}</TableCell>
        <TableCell align="right">{row.high_cycle ?? '—'}</TableCell>
        <TableCell align="right">{row.additional_parts ?? '—'}</TableCell>
        <TableCell align="right">{row.actual ?? '—'}</TableCell>
        <TableCell align="center" onClick={(e) => e.stopPropagation()}>
          <Tooltip title={isProduction ? 'Counted in totals — click to exclude (e.g. machine on leave that shift)' : 'Excluded from all totals — click to include again'}>
            <span>
              <Switch size="small" checked={isProduction} disabled={toggling} onChange={handleToggle} color="warning" />
            </span>
          </Tooltip>
        </TableCell>
      </TableRow>
      <TableRow>
        <TableCell colSpan={showNonMandatoryNet ? 17 : 18} sx={{ p: 0, borderBottom: expanded ? undefined : 'none' }}>
          <Collapse in={expanded} timeout="auto" unmountOnExit>
            {expanded && (
              <>
                <IdleReasonRows reasons={idleReasons} />
                <PartDetailRows parts={parts} cycleTime={cycleTime} />
              </>
            )}
          </Collapse>
        </TableCell>
      </TableRow>
      <ExtraIdleTimeCalculationDialog open={calcOpen} onClose={() => setCalcOpen(false)} pmsProgramReportId={row.id} />
    </>
  );
}

function IdleReasonRows({ reasons }) {
  if (reasons.length === 0) {
    return (
      <Box sx={{ p: 2, textAlign: 'center', color: TEXT_MUTED, fontSize: '13px' }}>
        No idle reasons recorded for this shift.
      </Box>
    );
  }

  return (
    <Box sx={{ p: 2, background: '#fafafa' }}>
      <Typography sx={{ fontWeight: 700, fontSize: '13px', mb: 1 }}>Idle Reasons</Typography>
      <TableContainer>
        <Table size="small">
          <TableHead>
            <TableRow sx={{ background: '#eceff3' }}>
              <TableCell sx={{ fontWeight: 700 }}>Reason</TableCell>
              <TableCell align="right" sx={{ fontWeight: 700 }}>Duration</TableCell>
              <TableCell align="right" sx={{ fontWeight: 700 }}>Threshold</TableCell>
              <TableCell align="right" sx={{ fontWeight: 700 }}>Excess</TableCell>
            </TableRow>
          </TableHead>
          <TableBody>
            {reasons.map((reason, idx) => (
              <TableRow key={`${reason.name}-${idx}`}>
                <TableCell>{reason.name || '—'}</TableCell>
                <TableCell align="right">{secondsToHms(reason.duration)}</TableCell>
                <TableCell align="right">{secondsToHms(reason.threshold)}</TableCell>
                <TableCell
                  align="right"
                  sx={{ color: Number(reason.excess) > 0 ? STATUS_CRITICAL : undefined, fontWeight: Number(reason.excess) > 0 ? 700 : 400 }}
                >
                  {secondsToHms(reason.excess)}
                </TableCell>
              </TableRow>
            ))}
          </TableBody>
        </Table>
      </TableContainer>
    </Box>
  );
}

function PartDetailRows({ parts, cycleTime }) {
  if (parts.length === 0) {
    return (
      <Box sx={{ p: 2, textAlign: 'center', color: TEXT_MUTED, fontSize: '13px' }}>
        No part detail saved for this shift.
      </Box>
    );
  }

  return (
    <Box sx={{ p: 2, background: '#f4f6f9' }}>
      <TableContainer>
        <Table size="small">
          <TableHead>
            <TableRow sx={{ background: '#eceff3' }}>
              <TableCell sx={{ fontWeight: 700 }}>Part ID</TableCell>
              <TableCell sx={{ fontWeight: 700 }}>Start</TableCell>
              <TableCell sx={{ fontWeight: 700 }}>End</TableCell>
              <TableCell align="right" sx={{ fontWeight: 700 }}>Total Run Time</TableCell>
              <TableCell align="right" sx={{ fontWeight: 700 }}>Total Idle Time</TableCell>
              <TableCell align="right" sx={{ fontWeight: 700 }}>Total Stop Time</TableCell>
              <TableCell align="right" sx={{ fontWeight: 700 }}>Total Alarm Time</TableCell>
              <TableCell align="right" sx={{ fontWeight: 700 }}>Total Parts</TableCell>
              <TableCell align="right" sx={{ fontWeight: 700 }}>Product Result</TableCell>
              <TableCell sx={{ fontWeight: 700 }}>Merged</TableCell>
              <TableCell align="center" sx={{ fontWeight: 700 }}>Cycle</TableCell>
            </TableRow>
          </TableHead>
          <TableBody>
            {parts.map((part, idx) => (
              <TableRow key={part.part_id ?? idx}>
                <TableCell>{part.part_id ?? '—'}</TableCell>
                <TableCell>{fmtTime(part.start_time)}</TableCell>
                <TableCell>{fmtTime(part.end_time)}</TableCell>
                <TableCell align="right">{secondsToHms(part.run_time)}</TableCell>
                <TableCell align="right">{secondsToHms(part.idle_time)}</TableCell>
                <TableCell align="right">{secondsToHms(part.stop_time)}</TableCell>
                <TableCell align="right">{secondsToHms(part.alarm_time)}</TableCell>
                <TableCell align="right">{part.total_parts ?? '—'}</TableCell>
                <TableCell align="right">{part.productresult ?? '—'}</TableCell>
                <TableCell>{part.is_merged_from_prev ? `Yes (${part.merged_count})` : 'No'}</TableCell>
                <TableCell align="center"><BucketChip bucket={cycleBucket(part, cycleTime)} /></TableCell>
              </TableRow>
            ))}
          </TableBody>
        </Table>
      </TableContainer>
    </Box>
  );
}

// Small "how was this calculated?" popup for auto cycle_time/handling_time
// - shows the full tolerance-clustering breakdown from getCycleTimeCalculation
// (every cluster found, biggest first), with the winning run_time cluster
// and winning idle_time cluster (within it) highlighted.
function ClusterTable({ title, clusters, winning, unit }) {
  return (
    <Box sx={{ mb: 2 }}>
      <Typography sx={{ fontSize: '12px', fontWeight: 700, mb: 0.5 }}>{title}</Typography>
      <Table size="small">
        <TableHead>
          <TableRow>
            <TableCell sx={{ fontWeight: 700, fontSize: '11px' }}>Range</TableCell>
            <TableCell align="right" sx={{ fontWeight: 700, fontSize: '11px' }}>Members</TableCell>
            <TableCell align="right" sx={{ fontWeight: 700, fontSize: '11px' }}>Average</TableCell>
          </TableRow>
        </TableHead>
        <TableBody>
          {clusters.map((c, idx) => {
            const isWinner = winning && c.range_low === winning.range_low && c.range_high === winning.range_high && c.members === winning.members;
            return (
              <TableRow key={idx} sx={{ background: isWinner ? '#eefaf0' : undefined }}>
                <TableCell sx={{ fontSize: '12px', fontWeight: isWinner ? 700 : 400 }}>
                  {c.range_low === c.range_high ? `${c.range_low}${unit}` : `${c.range_low}-${c.range_high}${unit}`}
                  {isWinner && <span style={{ color: '#0ca30c', fontWeight: 700 }}> (winner)</span>}
                </TableCell>
                <TableCell align="right" sx={{ fontSize: '12px', fontWeight: isWinner ? 700 : 400 }}>{c.members}</TableCell>
                <TableCell align="right" sx={{ fontSize: '12px', fontWeight: isWinner ? 700 : 400 }}>{c.avg}{unit}</TableCell>
              </TableRow>
            );
          })}
        </TableBody>
      </Table>
    </Box>
  );
}

function CycleTimeCalculationDialog({ open, onClose, programTransactionId }) {
  const [data, setData] = useState(null);
  const [loading, setLoading] = useState(false);
  const [error, setError] = useState('');

  useEffect(() => {
    if (!open || !programTransactionId) return;
    let cancelled = false;
    (async () => {
      setLoading(true);
      setError('');
      try {
        const result = await getCycleTimeCalculation(programTransactionId);
        if (!cancelled) setData(result);
      } catch (err) {
        if (!cancelled) setError('Failed to load calculation.');
      } finally {
        if (!cancelled) setLoading(false);
      }
    })();
    return () => { cancelled = true; };
  }, [open, programTransactionId]);

  return (
    <Dialog open={open} onClose={onClose} maxWidth="sm" fullWidth>
      <DialogTitle sx={{ display: 'flex', justifyContent: 'space-between', alignItems: 'center', pb: 1 }}>
        <Typography variant="h6" sx={{ fontWeight: 700, fontSize: '16px' }}>How Cycle Time / Handling Time Were Calculated</Typography>
        <IconButton onClick={onClose} size="small" sx={{ color: (theme) => theme.palette.grey[500] }}>
          <CloseIcon />
        </IconButton>
      </DialogTitle>
      <DialogContent dividers>
        {loading && (
          <Box sx={{ display: 'flex', justifyContent: 'center', p: 3 }}>
            <CircularProgress size={24} />
          </Box>
        )}
        {!loading && error && (
          <Typography sx={{ color: STATUS_CRITICAL, fontSize: '13px' }}>{error}</Typography>
        )}
        {!loading && !error && data && (
          <>
            <Typography sx={{ fontSize: '12px', color: TEXT_MUTED, mb: 2 }}>
              {data.total_parts} individual part readings were grouped into clusters (values within 10% of a cluster's smallest member count as the same real cycle). The biggest cluster is treated as the real, repeated cycle.
            </Typography>

            <Box sx={{ display: 'flex', gap: 2, mb: 2 }}>
              <Box sx={{ flex: 1, background: '#f4f6fb', borderRadius: 1, p: 1.5 }}>
                <Typography sx={{ fontSize: '11px', color: TEXT_MUTED }}>Cycle Time (current)</Typography>
                <Typography sx={{ fontSize: '18px', fontWeight: 700 }}>{secondsToHms(data.current_cycle_time)}</Typography>
                {data.current_cycle_time !== data.cycle_time && (
                  <Typography sx={{ fontSize: '11px', color: TEXT_MUTED }}>Would compute now: {secondsToHms(data.cycle_time)}</Typography>
                )}
              </Box>
              <Box sx={{ flex: 1, background: '#f4f6fb', borderRadius: 1, p: 1.5 }}>
                <Typography sx={{ fontSize: '11px', color: TEXT_MUTED }}>Handling Time (current)</Typography>
                <Typography sx={{ fontSize: '18px', fontWeight: 700 }}>{secondsToHms(data.current_handling_time)}</Typography>
                {data.current_handling_time !== data.handling_time && (
                  <Typography sx={{ fontSize: '11px', color: TEXT_MUTED }}>Would compute now: {secondsToHms(data.handling_time)}</Typography>
                )}
              </Box>
            </Box>

            <ClusterTable title="Run Time Clusters (top 10, all parts)" clusters={data.run_time_clusters} winning={data.winning_run_time_cluster} unit="s" />
            <ClusterTable title="Idle Time Clusters (top 10, within the winning run-time cluster only)" clusters={data.idle_time_clusters} winning={data.winning_idle_time_cluster} unit="s" />
          </>
        )}
      </DialogContent>
      <DialogActions>
        <Button onClick={onClose}>Close</Button>
      </DialogActions>
    </Dialog>
  );
}

function ExtraIdleTimeCalculationDialog({ open, onClose, pmsProgramReportId }) {
  const [data, setData] = useState(null);
  const [loading, setLoading] = useState(false);
  const [error, setError] = useState('');

  useEffect(() => {
    if (!open || !pmsProgramReportId) return;
    let cancelled = false;
    (async () => {
      setLoading(true);
      setError('');
      try {
        const result = await getExtraIdleTimeCalculation(pmsProgramReportId);
        if (!cancelled) setData(result);
      } catch (err) {
        if (!cancelled) setError('Failed to load calculation.');
      } finally {
        if (!cancelled) setLoading(false);
      }
    })();
    return () => { cancelled = true; };
  }, [open, pmsProgramReportId]);

  return (
    <Dialog open={open} onClose={onClose} maxWidth="sm" fullWidth>
      <DialogTitle sx={{ display: 'flex', justifyContent: 'space-between', alignItems: 'center', pb: 1 }}>
        <Typography variant="h6" sx={{ fontWeight: 700, fontSize: '16px' }}>How Extra Idle Time Was Calculated</Typography>
        <IconButton onClick={onClose} size="small" sx={{ color: (theme) => theme.palette.grey[500] }}>
          <CloseIcon />
        </IconButton>
      </DialogTitle>
      <DialogContent dividers>
        {loading && (
          <Box sx={{ display: 'flex', justifyContent: 'center', p: 3 }}>
            <CircularProgress size={24} />
          </Box>
        )}
        {!loading && error && (
          <Typography sx={{ color: STATUS_CRITICAL, fontSize: '13px' }}>{error}</Typography>
        )}
        {!loading && !error && data && (
          <>
            <Typography sx={{ fontSize: '12px', color: TEXT_MUTED, mb: 2 }}>
              For a part that actually produced something (productresult &gt; 0): Extra Idle Time = max(0, part idle_time − Target Handling Time). For a part with no result at all (productresult = 0 — scrapped/aborted): the FULL idle_time counts, unclamped, since none of that idle went toward producing anything. Summed across all {data.total_parts} parts ({data.produced_parts} produced, {data.unproduced_parts} not; {data.parts_contributing} contributing &gt; 0). Extra Idle Time (Net) subtracts Mandatory Idle Time (enabled-reason idle, plus the within-threshold portion of every other idle reason). Cost = (time ÷ 3600) × the machine's hourly rate, same as every other idle cost figure.
            </Typography>

            <Box sx={{ display: 'flex', gap: 2, mb: 2 }}>
              <Box sx={{ flex: 1, background: '#f4f6fb', borderRadius: 1, p: 1.5 }}>
                <Typography sx={{ fontSize: '11px', color: TEXT_MUTED }}>Target Handling Time</Typography>
                <Typography sx={{ fontSize: '18px', fontWeight: 700 }}>{secondsToHms(data.target_handling_time)}</Typography>
              </Box>
              <Box sx={{ flex: 1, background: '#f4f6fb', borderRadius: 1, p: 1.5 }}>
                <Typography sx={{ fontSize: '11px', color: TEXT_MUTED }}>Extra Idle Time</Typography>
                <Typography sx={{ fontSize: '18px', fontWeight: 700 }}>{secondsToHms(data.extra_idle_time)}</Typography>
                <Typography sx={{ fontSize: '11px', color: TEXT_MUTED }}>Cost: {fmtCost(data.extra_idle_cost)}</Typography>
              </Box>
              <Box sx={{ flex: 1, background: '#f4f6fb', borderRadius: 1, p: 1.5 }}>
                <Typography sx={{ fontSize: '11px', color: TEXT_MUTED }}>Extra Idle Time (Net)</Typography>
                <Typography sx={{ fontSize: '18px', fontWeight: 700 }}>{secondsToHms(data.extra_idle_time_net)}</Typography>
                <Typography sx={{ fontSize: '11px', color: TEXT_MUTED }}>Cost: {fmtCost(data.extra_idle_cost_net)}</Typography>
              </Box>
            </Box>

            <Box sx={{ mb: 2 }}>
              <Typography sx={{ fontSize: '12px', fontWeight: 700, mb: 0.5 }}>
                Mandatory Idle Time by Reason (total {secondsToHms(data.mandatory_idle_time)})
              </Typography>
              {data.mandatory_reasons.length === 0 ? (
                <Typography sx={{ fontSize: '12px', color: TEXT_MUTED }}>No idle reasons recorded for this shift.</Typography>
              ) : (
                <Table size="small">
                  <TableHead>
                    <TableRow>
                      <TableCell sx={{ fontWeight: 700, fontSize: '11px' }}>Reason</TableCell>
                      <TableCell align="right" sx={{ fontWeight: 700, fontSize: '11px' }}>Duration</TableCell>
                      <TableCell align="right" sx={{ fontWeight: 700, fontSize: '11px' }}>Threshold</TableCell>
                      <TableCell align="right" sx={{ fontWeight: 700, fontSize: '11px' }}>Mandatory</TableCell>
                    </TableRow>
                  </TableHead>
                  <TableBody>
                    {data.mandatory_reasons.map((r, idx) => (
                      <TableRow key={idx}>
                        <TableCell sx={{ fontSize: '12px' }}>{r.name || '—'}</TableCell>
                        <TableCell align="right" sx={{ fontSize: '12px' }}>{secondsToHms(r.duration)}</TableCell>
                        <TableCell align="right" sx={{ fontSize: '12px' }}>{secondsToHms(r.threshold)}</TableCell>
                        <TableCell align="right" sx={{ fontSize: '12px', fontWeight: 700 }}>{secondsToHms(r.mandatory)}</TableCell>
                      </TableRow>
                    ))}
                  </TableBody>
                </Table>
              )}
            </Box>

            <Box>
              <Typography sx={{ fontSize: '12px', fontWeight: 700, mb: 0.5 }}>
                Contributing Parts (top {data.part_breakdown.length} of {data.parts_contributing})
              </Typography>
              <Table size="small">
                <TableHead>
                  <TableRow>
                    <TableCell sx={{ fontWeight: 700, fontSize: '11px' }}>Part</TableCell>
                    <TableCell align="center" sx={{ fontWeight: 700, fontSize: '11px' }}>Produced</TableCell>
                    <TableCell align="right" sx={{ fontWeight: 700, fontSize: '11px' }}>Total Idle Time</TableCell>
                    <TableCell align="right" sx={{ fontWeight: 700, fontSize: '11px' }}>Extra</TableCell>
                  </TableRow>
                </TableHead>
                <TableBody>
                  {data.part_breakdown.map((p, idx) => (
                    <TableRow key={idx}>
                      <TableCell sx={{ fontSize: '12px' }}>{p.part_id}</TableCell>
                      <TableCell align="center" sx={{ fontSize: '12px', color: p.produced ? undefined : STATUS_CRITICAL }}>{p.produced ? 'Yes' : 'No'}</TableCell>
                      <TableCell align="right" sx={{ fontSize: '12px' }}>{secondsToHms(p.idle_time)}</TableCell>
                      <TableCell align="right" sx={{ fontSize: '12px', fontWeight: 700 }}>{secondsToHms(p.extra)}</TableCell>
                    </TableRow>
                  ))}
                </TableBody>
              </Table>
            </Box>
          </>
        )}
      </DialogContent>
      <DialogActions>
        <Button onClick={onClose}>Close</Button>
      </DialogActions>
    </Dialog>
  );
}

function TransactionRow({ row, machineName, onOpenReason, onOpenEdit, onDataChanged, showNonMandatoryNet }) {
  const [expanded, setExpanded] = useState(false);
  const [calcOpen, setCalcOpen] = useState(false);
  const [unlocking, setUnlocking] = useState(false);
  const hasShiftData = row.has_shift_data !== false;
  const hasProgram = row.has_program !== false;
  const isDisabled = !hasShiftData || !hasProgram;

  const handleUnlock = async (e) => {
    e.stopPropagation();
    if (unlocking) return;
    if (!window.confirm('Reset Cycle Time and Handling Time back to the auto-computed values? This cannot be undone.')) return;
    setUnlocking(true);
    try {
      await unlockCycleTime(row.id);
      if (onDataChanged) await onDataChanged();
    } finally {
      setUnlocking(false);
    }
  };

  return (
    <>
      <TableRow
        hover
        sx={{ cursor: 'pointer', opacity: isDisabled ? 0.5 : 1, background: isDisabled ? '#f4f4f4' : undefined }}
        onClick={() => setExpanded((v) => !v)}
      >
        <TableCell>
          <IconButton size="small">
            {expanded ? <KeyboardArrowUpIcon /> : <KeyboardArrowDownIcon />}
          </IconButton>
        </TableCell>
        <TableCell>{row.program_name || '—'}</TableCell>
        <TableCell align="right">
          {secondsToHms(row.cycle_time)}
          <Tooltip title="How was this calculated?">
            <IconButton
              size="small"
              onClick={(e) => { e.stopPropagation(); setCalcOpen(true); }}
              sx={{ p: 0.25, ml: 0.25 }}
            >
              <InfoOutlinedIcon sx={{ fontSize: 14, color: TEXT_MUTED }} />
            </IconButton>
          </Tooltip>
          {row.manual_cycle_time ? (
            <Tooltip title="Manually set — click to reset to auto-computed">
              <IconButton size="small" onClick={handleUnlock} disabled={unlocking} sx={{ p: 0.25, ml: 0.25 }}>
                <LockIcon sx={{ fontSize: 14, color: STATUS_CRITICAL }} />
              </IconButton>
            </Tooltip>
          ) : (
            <Tooltip title="Auto-computed — the algorithm keeps refining this">
              <LockOpenIcon sx={{ fontSize: 14, color: TEXT_MUTED, verticalAlign: 'middle', ml: 0.25 }} />
            </Tooltip>
          )}
        </TableCell>
        <TableCell align="right">{secondsToHms(row.handling_time)}</TableCell>
        <TableCell align="right">{secondsToHms(row.setup_time)}</TableCell>
        <TableCell align="right">{secondsToHms(row.setting_time)}</TableCell>
        <TableCell
          align="right"
          sx={{ color: Number(row.extra_setting_time) > 0 ? STATUS_CRITICAL : undefined, fontWeight: Number(row.extra_setting_time) > 0 ? 700 : 400 }}
        >
          {secondsToHms(row.extra_setting_time)}
        </TableCell>
        <TableCell align="right">{secondsToHms(row.total_run_time)}</TableCell>
        <TableCell align="right">{secondsToHms(row.total_idle_time)}</TableCell>
        <TableCell align="right">{secondsToHms(row.total_stop_time)}</TableCell>
        <TableCell align="right">{secondsToHms(row.total_alarm_time)}</TableCell>
        {showNonMandatoryNet && (
          <TableCell
            align="right"
            sx={{ color: Number(row.excess_reason_time) > 0 ? STATUS_CRITICAL : undefined, fontWeight: Number(row.excess_reason_time) > 0 ? 700 : 400 }}
          >
            {secondsToHms(row.excess_reason_time)}
          </TableCell>
        )}
        {showNonMandatoryNet && (
          <TableCell
            align="right"
            sx={{ color: Number(row.non_mandatory_idle_time_net) > 0 ? STATUS_CRITICAL : undefined, fontWeight: Number(row.non_mandatory_idle_time_net) > 0 ? 700 : 400 }}
          >
            {secondsToHms(row.non_mandatory_idle_time_net)}
          </TableCell>
        )}
        {showNonMandatoryNet && <TableCell align="right">{fmtCost(row.non_mandatory_idle_cost_net)}</TableCell>}
        {!showNonMandatoryNet && (
          <TableCell
            align="right"
            sx={{ color: Number(row.extra_idle_time) > 0 ? STATUS_CRITICAL : undefined, fontWeight: Number(row.extra_idle_time) > 0 ? 700 : 400 }}
          >
            {secondsToHms(row.extra_idle_time)}
          </TableCell>
        )}
        {!showNonMandatoryNet && (
          <TableCell
            align="right"
            sx={{ color: Number(row.extra_idle_time_net) > 0 ? STATUS_CRITICAL : undefined, fontWeight: Number(row.extra_idle_time_net) > 0 ? 700 : 400 }}
          >
            {secondsToHms(row.extra_idle_time_net)}
          </TableCell>
        )}
        {!showNonMandatoryNet && <TableCell align="right">{fmtCost(row.extra_idle_cost_net)}</TableCell>}
        <TableCell align="right">{row.low_cycle ?? '—'}</TableCell>
        <TableCell align="right">{row.normal_cycle ?? '—'}</TableCell>
        <TableCell align="right">{row.high_cycle ?? '—'}</TableCell>
        <TableCell align="right">{row.additional_parts ?? '—'}</TableCell>
        <TableCell align="right">{row.part_count ?? '—'}</TableCell>
        <TableCell>{fmtTime(row.start_time)}</TableCell>
        <TableCell>{fmtTime(row.end_time)}</TableCell>
        <TableCell align="center" onClick={(e) => e.stopPropagation()}>
          <Button
            variant="outlined"
            size="small"
            onClick={() => onOpenReason(machineName, row.start_time, row.end_time)}
            sx={{
              textTransform: 'none', fontSize: '11px', py: 0.25, px: 1, whiteSpace: 'nowrap',
              color: '#ff9800', borderColor: '#ff9800',
              '&:hover': { borderColor: '#e65100', backgroundColor: 'rgba(255, 152, 0, 0.08)' },
            }}
          >
            Reason
          </Button>
        </TableCell>
        <TableCell align="center" onClick={(e) => e.stopPropagation()}>
          <Button
            variant="outlined"
            size="small"
            onClick={() => onOpenEdit(row)}
            sx={{
              textTransform: 'none', fontSize: '11px', py: 0.25, px: 1, whiteSpace: 'nowrap',
              color: '#2563eb', borderColor: '#2563eb',
              '&:hover': { borderColor: '#1d4ed8', backgroundColor: 'rgba(37, 99, 235, 0.08)' },
            }}
          >
            Update
          </Button>
        </TableCell>
      </TableRow>
      <TableRow>
        <TableCell colSpan={23} sx={{ p: 0, borderBottom: expanded ? undefined : 'none' }}>
          <Collapse in={expanded} timeout="auto" unmountOnExit>
            {expanded && (
              <ShiftRows transactionId={row.id} startTimeMs={row.start_time} endTimeMs={row.end_time} onDataChanged={onDataChanged} cycleTime={row.cycle_time} showNonMandatoryNet={showNonMandatoryNet} />
            )}
          </Collapse>
        </TableCell>
      </TableRow>
      <CycleTimeCalculationDialog open={calcOpen} onClose={() => setCalcOpen(false)} programTransactionId={row.id} />
    </>
  );
}

// Drilldown level 1-2: program_transaction (level 1) -> pms_program_report
// (level 2, on row click), via GET /machine/program-transactions and
// GET /machine/pms-program-report-by-transaction (Machine.controller.ts /
// Machine.service.ts on the tb-node-mtlinki backend). Scoped to one machine,
// nested inside a MachineSummaryRow (level 0) below.
function MachineTransactionsPanel({ machineName, filters, onDataChanged: onMachineDataChanged, showNonMandatoryNet }) {
  const [rows, setRows] = useState([]);
  const [loading, setLoading] = useState(false);
  const [error, setError] = useState('');
  const [paretoModalState, setParetoModalState] = useState({ open: false, machine: '', loading: false, error: '', data: [] });
  const [editDialogState, setEditDialogState] = useState({ open: false, row: null });

  const handleOpenEdit = (row) => setEditDialogState({ open: true, row });

  const handleOpenReason = async (machine, windowStart, windowEnd) => {
    setParetoModalState({ open: true, machine, loading: true, error: '', data: [] });
    try {
      const response = await getExcessReasonBreakdown(machine, windowStart, windowEnd);
      const list = Array.isArray(response) ? response : response?.data || [];
      setParetoModalState((prev) => ({ ...prev, loading: false, data: list }));
    } catch (err) {
      setParetoModalState((prev) => ({ ...prev, loading: false, error: 'Failed to load excess reason breakdown. Please try again.' }));
    }
  };

  const loadTransactions = useCallback(async () => {
    if (!filters || !machineName) return;
    setLoading(true);
    setError('');
    try {
      const data = await getProgramTransactions(machineName, filters.startTimeMs, filters.endTimeMs);
      setRows(Array.isArray(data) ? data : []);
    } catch (err) {
      setError('Failed to load program transactions. Please try again.');
    } finally {
      setLoading(false);
    }
  }, [filters, machineName]);

  useEffect(() => {
    let cancelled = false;
    (async () => {
      if (cancelled) return;
      await loadTransactions();
    })();
    return () => { cancelled = true; };
  }, [loadTransactions]);

  if (!filters || !machineName) {
    return <StatusBox>Select a machine and click Submit to load program transactions.</StatusBox>;
  }
  if (loading) {
    return (
      <StatusBox>
        <CircularProgress size={28} />
      </StatusBox>
    );
  }
  if (error) {
    return <StatusBox>{error}</StatusBox>;
  }
  if (rows.length === 0) {
    return <StatusBox>No program transactions for the selected filters.</StatusBox>;
  }

  return (
    <Box sx={{ border: `1px solid ${BORDER}`, borderRadius: 2, background: '#ffffff', overflow: 'hidden' }}>
      <Box sx={{ p: 2, borderBottom: `1px solid ${BORDER}` }}>
        <Typography sx={{ fontWeight: 700, fontSize: '16px' }}>Program Transactions</Typography>
        <Typography sx={{ fontSize: '13px', color: TEXT_MUTED }}>
          {machineName} — click a row to see its saved shift report
        </Typography>
      </Box>
      <TableContainer>
        <Table size="small">
          <TableHead>
            <TableRow sx={{ background: '#f9f9f7' }}>
              <TableCell />
              <TableCell sx={{ fontWeight: 700 }}>Program Name</TableCell>
              <TableCell align="right" sx={{ fontWeight: 700 }}>Target Machining Time</TableCell>
              <TableCell align="right" sx={{ fontWeight: 700 }}>Target Handling Time</TableCell>
              <TableCell align="right" sx={{ fontWeight: 700 }}>Target Setup Time</TableCell>
              <TableCell align="right" sx={{ fontWeight: 700 }}>Setting Time</TableCell>
              <TableCell align="right" sx={{ fontWeight: 700 }}>Extra Setting Time</TableCell>
              <TableCell align="right" sx={{ fontWeight: 700 }}>Total Run Time</TableCell>
              <TableCell align="right" sx={{ fontWeight: 700 }}>Total Idle Time</TableCell>
              <TableCell align="right" sx={{ fontWeight: 700 }}>Total Stop Time</TableCell>
              <TableCell align="right" sx={{ fontWeight: 700 }}>Total Alarm Time</TableCell>
              {showNonMandatoryNet && <TableCell align="right" sx={{ fontWeight: 700 }}>Non Mandatory Idle Time</TableCell>}
              {showNonMandatoryNet && <TableCell align="right" sx={{ fontWeight: 700 }}>Non Mandatory Idle Time (Net)</TableCell>}
              {showNonMandatoryNet && <TableCell align="right" sx={{ fontWeight: 700 }}>Non Mandatory Idle Cost (Net)</TableCell>}
              {!showNonMandatoryNet && <TableCell align="right" sx={{ fontWeight: 700 }}>Non Mandatory Idle Time1</TableCell>}
              {!showNonMandatoryNet && <TableCell align="right" sx={{ fontWeight: 700 }}>Non Mandatory Idle Time1 (Net)</TableCell>}
              {!showNonMandatoryNet && <TableCell align="right" sx={{ fontWeight: 700 }}>Non Mandatory Idle Cost1 (Net)</TableCell>}
              <TableCell align="right" sx={{ fontWeight: 700 }}>Lower Cycle Time</TableCell>
              <TableCell align="right" sx={{ fontWeight: 700 }}>Normal Cycle Time</TableCell>
              <TableCell align="right" sx={{ fontWeight: 700 }}>Higher Cycle Time</TableCell>
              <TableCell align="right" sx={{ fontWeight: 700 }}>Additional Parts</TableCell>
              <TableCell align="right" sx={{ fontWeight: 700 }}>Actual Production</TableCell>
              <TableCell sx={{ fontWeight: 700 }}>Start</TableCell>
              <TableCell sx={{ fontWeight: 700 }}>End</TableCell>
              <TableCell align="center" sx={{ fontWeight: 700 }}>Reason</TableCell>
              <TableCell align="center" sx={{ fontWeight: 700 }}>Update</TableCell>
            </TableRow>
          </TableHead>
          <TableBody>
            {rows.map((row) => (
              <TransactionRow
                key={row.id}
                row={row}
                machineName={machineName}
                onOpenReason={handleOpenReason}
                onOpenEdit={handleOpenEdit}
                onDataChanged={async () => {
                  await loadTransactions();
                  if (onMachineDataChanged) await onMachineDataChanged();
                }}
                showNonMandatoryNet={showNonMandatoryNet}
              />
            ))}
          </TableBody>
        </Table>
      </TableContainer>

      <IdleReasonParetoModal
        open={paretoModalState.open}
        onClose={() => setParetoModalState((prev) => ({ ...prev, open: false }))}
        machine={paretoModalState.machine}
        loading={paretoModalState.loading}
        error={paretoModalState.error}
        data={paretoModalState.data}
      />

      <EditTimesDialog
        open={editDialogState.open}
        row={editDialogState.row}
        onClose={() => setEditDialogState({ open: false, row: null })}
        onSaved={() => {
          setEditDialogState({ open: false, row: null });
          loadTransactions();
        }}
      />
    </Box>
  );
}

// Drilldown level 0: one row per machine, summed across every one of that
// machine's program_transaction rows in the window (GET
// /machine/program-transactions-machine-summary). Expanding a row mounts
// MachineTransactionsPanel (level 1-2) scoped to just that machine.
function MachineSummaryRow({ row, filters, onOpenReason, onDataChanged, showNonMandatoryNet }) {
  const [expanded, setExpanded] = useState(false);
  return (
    <>
      <TableRow hover sx={{ cursor: 'pointer' }} onClick={() => setExpanded((v) => !v)}>
        <TableCell>
          <IconButton size="small">
            {expanded ? <KeyboardArrowUpIcon /> : <KeyboardArrowDownIcon />}
          </IconButton>
        </TableCell>
        <TableCell>{row.machine_name}</TableCell>
        <TableCell align="right">{row.transaction_count ?? '—'}</TableCell>
        <TableCell align="right">{secondsToHms(row.setting_time)}</TableCell>
        <TableCell
          align="right"
          sx={{ color: Number(row.extra_setting_time) > 0 ? STATUS_CRITICAL : undefined, fontWeight: Number(row.extra_setting_time) > 0 ? 700 : 400 }}
        >
          {secondsToHms(row.extra_setting_time)}
        </TableCell>
        <TableCell align="right">{secondsToHms(row.total_run_time)}</TableCell>
        <TableCell align="right">{secondsToHms(row.total_idle_time)}</TableCell>
        <TableCell align="right">{secondsToHms(row.total_stop_time)}</TableCell>
        <TableCell align="right">{secondsToHms(row.total_alarm_time)}</TableCell>
        {showNonMandatoryNet && (
          <TableCell
            align="right"
            sx={{ color: Number(row.excess_reason_time) > 0 ? STATUS_CRITICAL : undefined, fontWeight: Number(row.excess_reason_time) > 0 ? 700 : 400 }}
          >
            {secondsToHms(row.excess_reason_time)}
          </TableCell>
        )}
        {showNonMandatoryNet && (
          <TableCell
            align="right"
            sx={{ color: Number(row.non_mandatory_idle_time_net) > 0 ? STATUS_CRITICAL : undefined, fontWeight: Number(row.non_mandatory_idle_time_net) > 0 ? 700 : 400 }}
          >
            {secondsToHms(row.non_mandatory_idle_time_net)}
          </TableCell>
        )}
        {showNonMandatoryNet && <TableCell align="right">{fmtCost(row.non_mandatory_idle_cost_net)}</TableCell>}
        {!showNonMandatoryNet && (
          <TableCell
            align="right"
            sx={{ color: Number(row.extra_idle_time) > 0 ? STATUS_CRITICAL : undefined, fontWeight: Number(row.extra_idle_time) > 0 ? 700 : 400 }}
          >
            {secondsToHms(row.extra_idle_time)}
          </TableCell>
        )}
        {!showNonMandatoryNet && (
          <TableCell
            align="right"
            sx={{ color: Number(row.extra_idle_time_net) > 0 ? STATUS_CRITICAL : undefined, fontWeight: Number(row.extra_idle_time_net) > 0 ? 700 : 400 }}
          >
            {secondsToHms(row.extra_idle_time_net)}
          </TableCell>
        )}
        {!showNonMandatoryNet && <TableCell align="right">{fmtCost(row.extra_idle_cost_net)}</TableCell>}
        <TableCell align="right">{row.low_cycle ?? '—'}</TableCell>
        <TableCell align="right">{row.normal_cycle ?? '—'}</TableCell>
        <TableCell align="right">{row.high_cycle ?? '—'}</TableCell>
        <TableCell align="right">{row.additional_parts ?? '—'}</TableCell>
        <TableCell align="right">{row.part_count ?? '—'}</TableCell>
        <TableCell align="center" onClick={(e) => e.stopPropagation()}>
          <Button
            variant="outlined"
            size="small"
            onClick={() => onOpenReason(row.machine_name, filters.startTimeMs, filters.endTimeMs)}
            sx={{
              textTransform: 'none', fontSize: '11px', py: 0.25, px: 1, whiteSpace: 'nowrap',
              color: '#ff9800', borderColor: '#ff9800',
              '&:hover': { borderColor: '#e65100', backgroundColor: 'rgba(255, 152, 0, 0.08)' },
            }}
          >
            Reason
          </Button>
        </TableCell>
      </TableRow>
      <TableRow>
        <TableCell colSpan={18} sx={{ p: 0, borderBottom: expanded ? undefined : 'none' }}>
          <Collapse in={expanded} timeout="auto" unmountOnExit>
            {expanded && (
              <Box sx={{ p: 2, background: '#fafafa' }}>
                <MachineTransactionsPanel machineName={row.machine_name} filters={filters} onDataChanged={onDataChanged} showNonMandatoryNet={showNonMandatoryNet} />
              </Box>
            )}
          </Collapse>
        </TableCell>
      </TableRow>
    </>
  );
}

// Method 4: drilldown sourced from program_transaction / pms_program_report,
// via Machine.controller.ts / Machine.service.ts on the tb-node-mtlinki
// backend. Machine (cumulative, this component) -> program_transaction ->
// pms_program_report, matching the 3-level shape of the other drilldown
// chains in DRILLDOWN.md. Covers every machine currently selected in the
// filter bar (filters.machines), not just the first one.
export default function ProgramTransactionReportTab({ filters }) {
  const [rows, setRows] = useState([]);
  const [loading, setLoading] = useState(false);
  const [error, setError] = useState('');
  const [paretoModalState, setParetoModalState] = useState({ open: false, machine: '', loading: false, error: '', data: [] });
  // Which "Net" idle family the machine/transaction tables show - the two
  // are independent measures (reason-based vs part-based, see their own
  // comments), shown one at a time to avoid a wall of near-duplicate
  // columns. true = Non Mandatory Idle Time (Net) + its cost, false = Extra
  // Idle Time (Net) + its cost. The always-shown base values (Non Mandatory
  // Idle Time, Extra Idle Time) aren't affected by this toggle.
  const [showNonMandatoryNet, setShowNonMandatoryNet] = useState(true);

  const machineNames = filters?.machines || [];

  const handleOpenReason = async (machine, windowStart, windowEnd) => {
    setParetoModalState({ open: true, machine, loading: true, error: '', data: [] });
    try {
      const response = await getExcessReasonBreakdown(machine, windowStart, windowEnd);
      const list = Array.isArray(response) ? response : response?.data || [];
      setParetoModalState((prev) => ({ ...prev, loading: false, data: list }));
    } catch (err) {
      setParetoModalState((prev) => ({ ...prev, loading: false, error: 'Failed to load excess reason breakdown. Please try again.' }));
    }
  };

  const loadMachineSummaries = useCallback(async () => {
    if (!filters || machineNames.length === 0) return;
    setLoading(true);
    setError('');
    try {
      const data = await getMachineProgramSummary(machineNames, filters.startTimeMs, filters.endTimeMs);
      setRows(Array.isArray(data) ? data : []);
    } catch (err) {
      setError('Failed to load machine summary. Please try again.');
    } finally {
      setLoading(false);
    }
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [filters, machineNames.join(',')]);

  useEffect(() => {
    let cancelled = false;
    (async () => {
      if (cancelled) return;
      await loadMachineSummaries();
    })();
    return () => { cancelled = true; };
  }, [loadMachineSummaries]);

  if (!filters || machineNames.length === 0) {
    return <StatusBox>Select a machine and click Submit to load program transactions.</StatusBox>;
  }
  if (loading) {
    return (
      <StatusBox>
        <CircularProgress size={28} />
      </StatusBox>
    );
  }
  if (error) {
    return <StatusBox>{error}</StatusBox>;
  }
  if (rows.length === 0) {
    return <StatusBox>No program transactions for the selected filters.</StatusBox>;
  }

  return (
    <Box sx={{ border: `1px solid ${BORDER}`, borderRadius: 2, background: '#ffffff', overflow: 'hidden' }}>
      <Box sx={{ p: 2, borderBottom: `1px solid ${BORDER}`, display: 'flex', justifyContent: 'space-between', alignItems: 'flex-start' }}>
        <Box>
          <Typography sx={{ fontWeight: 700, fontSize: '16px' }}>Program Transactions</Typography>
          <Typography sx={{ fontSize: '13px', color: TEXT_MUTED }}>
            Cumulative by machine — click a row to see its program transactions
          </Typography>
        </Box>
        <Box sx={{ display: 'flex', alignItems: 'center', gap: 1 }}>
          <Typography sx={{ fontSize: '12px', color: showNonMandatoryNet ? TEXT_MUTED : undefined, fontWeight: showNonMandatoryNet ? 400 : 700 }}>
            By Part
          </Typography>
          <Switch size="small" checked={showNonMandatoryNet} onChange={(e) => setShowNonMandatoryNet(e.target.checked)} />
          <Typography sx={{ fontSize: '12px', color: showNonMandatoryNet ? undefined : TEXT_MUTED, fontWeight: showNonMandatoryNet ? 700 : 400 }}>
            By Reason
          </Typography>
        </Box>
      </Box>
      <TableContainer>
        <Table size="small">
          <TableHead>
            <TableRow sx={{ background: '#f9f9f7' }}>
              <TableCell />
              <TableCell sx={{ fontWeight: 700 }}>Machine</TableCell>
              <TableCell align="right" sx={{ fontWeight: 700 }}>Type of Component</TableCell>
              <TableCell align="right" sx={{ fontWeight: 700 }}>Setting Time</TableCell>
              <TableCell align="right" sx={{ fontWeight: 700 }}>Extra Setting Time</TableCell>
              <TableCell align="right" sx={{ fontWeight: 700 }}>Total Run Time</TableCell>
              <TableCell align="right" sx={{ fontWeight: 700 }}>Total Idle Time</TableCell>
              <TableCell align="right" sx={{ fontWeight: 700 }}>Total Stop Time</TableCell>
              <TableCell align="right" sx={{ fontWeight: 700 }}>Total Alarm Time</TableCell>
              {showNonMandatoryNet && <TableCell align="right" sx={{ fontWeight: 700 }}>Non Mandatory Idle Time</TableCell>}
              {showNonMandatoryNet && <TableCell align="right" sx={{ fontWeight: 700 }}>Non Mandatory Idle Time (Net)</TableCell>}
              {showNonMandatoryNet && <TableCell align="right" sx={{ fontWeight: 700 }}>Non Mandatory Idle Cost (Net)</TableCell>}
              {!showNonMandatoryNet && <TableCell align="right" sx={{ fontWeight: 700 }}>Non Mandatory Idle Time1</TableCell>}
              {!showNonMandatoryNet && <TableCell align="right" sx={{ fontWeight: 700 }}>Non Mandatory Idle Time1 (Net)</TableCell>}
              {!showNonMandatoryNet && <TableCell align="right" sx={{ fontWeight: 700 }}>Non Mandatory Idle Cost1 (Net)</TableCell>}
              <TableCell align="right" sx={{ fontWeight: 700 }}>Lower Cycle Time</TableCell>
              <TableCell align="right" sx={{ fontWeight: 700 }}>Normal Cycle Time</TableCell>
              <TableCell align="right" sx={{ fontWeight: 700 }}>Higher Cycle Time</TableCell>
              <TableCell align="right" sx={{ fontWeight: 700 }}>Additional Parts</TableCell>
              <TableCell align="right" sx={{ fontWeight: 700 }}>Actual Production</TableCell>
              <TableCell align="center" sx={{ fontWeight: 700 }}>Reason</TableCell>
            </TableRow>
          </TableHead>
          <TableBody>
            {rows.map((row) => (
              <MachineSummaryRow key={row.machine_name} row={row} filters={filters} onOpenReason={handleOpenReason} onDataChanged={loadMachineSummaries} showNonMandatoryNet={showNonMandatoryNet} />
            ))}
          </TableBody>
        </Table>
      </TableContainer>

      <IdleReasonParetoModal
        open={paretoModalState.open}
        onClose={() => setParetoModalState((prev) => ({ ...prev, open: false }))}
        machine={paretoModalState.machine}
        loading={paretoModalState.loading}
        error={paretoModalState.error}
        data={paretoModalState.data}
      />
    </Box>
  );
}
